import { Org as AdminOrg, GradProgram } from 'api/orgAdmin';
import { ListingMini, OrgMini } from 'api/types/orgs-mini.types';
import { OrgType } from 'api/types/orgs.types';
import {
  FilestackImageData,
  FilestackImageWithTextData,
  RemoteOptions,
} from 'api/types/shared.types';
import { Volop } from 'api/volops';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';
import { getJson, postJson } from 'utils/http';

export type Address = {
  locationHidden?: boolean;
  line1: string | null | undefined;
  line2: string | null | undefined;
  city: string;
  state: string;
  stateCode: string;
  zipcode: string | null | undefined;
  country: string;
  latitude: number;
  longitude: number;
  description: string | null | undefined;
  full: string;
};
export type Org = {
  id: string;
  created: string;
  name: string;
  orgType: OrgType;
  description: string;
  descriptionTruncated: string;
  email: string;
  phone: string;
  links: Array<string>;
  address: Address;
  url: Record<UserLocale, string>;
  areasOfFocus: Array<string>;
  areasOfFocusKeys: Array<string>;
  jobs: Array<ListingMini>;
  internships: Array<ListingMini>;
  volops: Array<ListingMini>;
  idealistDayEvents: Array<ListingMini>;
  banner: FilestackImageData;
  logo: FilestackImageData;
  images: Array<FilestackImageWithTextData>;
  isFrozen?: boolean;
  isPublished?: boolean;
  is501c3?: boolean;
  orgSize?: string;
  relatedJobAreas?: boolean;
  relatedJobCount?: number;
  clients?: string;
  services?: string;
  isBCorp?: boolean;
  hiring?: boolean;
};
export type Job = {
  id: string;
  type: 'JOB';
  name: string;
  org: OrgMini;
  hasOrgNameOverride: boolean;
  orgNameOverride: string | null | undefined;
  orgLogoOverride: FilestackImageData | null | undefined;
  isPostedAnonymously: boolean;
  description: string;
  descriptionTruncated: string;
  address: Address;
  hasAts: boolean;
  requestedAttachments: Array<{
    label: string;
    isRequired: boolean;
    type: 'FILE' | 'URL';
  }>;
  applyEmail: string | null | undefined;
  applyUrl: string | null | undefined;
  applyText: string | null | undefined;
  applyTextTruncated: string | null | undefined;
  benefits: string | null | undefined;
  benefitsTruncated: string | null | undefined;
  salaryMinimum: number | null | undefined;
  salaryMaximum: number | null | undefined;
  salaryDetails: string | null | undefined;
  salaryCurrency: string | null | undefined;
  salaryPeriod: string | null | undefined;
  professionalLevel: string;
  languageProficiency: string | null | undefined;
  languageProficiencyTruncated: string | null | undefined;
  education: string;
  applicationDeadline: string | null | undefined;
  startDate: string | null | undefined;
  isFullTime: boolean;
  isTemporary: boolean;
  isContract: boolean;
  remoteOptions: RemoteOptions;
  url: Record<string, string>;
  firstPublished: string;
  isPublished: boolean;
};
export type Internship = {
  id: string;
  type: 'INTERNSHIP';
  name: string;
  org: OrgMini;
  hasOrgNameOverride: boolean;
  orgNameOverride: string | null | undefined;
  orgLogoOverride: FilestackImageData | null | undefined;
  isPostedAnonymously: boolean;
  description: string;
  descriptionTruncated: string;
  address: Address;
  paymentOk: boolean;
  hasAts: boolean;
  requestedAttachments: Array<{
    label: string;
    isRequired: boolean;
    type: 'FILE' | 'URL';
  }>;
  applyEmail: string | null | undefined;
  applyUrl: string | null | undefined;
  applyText: string | null | undefined;
  applyTextTruncated: string | null | undefined;
  academicCredits: boolean | null | undefined;
  benefits: string | null | undefined;
  benefitsTruncated: string | null | undefined;
  payment: string | null | undefined;
  wage: string | null | undefined;
  remoteOptions: RemoteOptions;
  hoursPerWeek: number | null | undefined;
  applicationDeadline: string | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  url: Record<string, string>;
  firstPublished: string;
  isPublished: boolean;
};
export type Listing = Job | Internship | Volop;
export type AreasOfFocus = Array<{
  id: string;
  text: string;
  usageRanking: string;
}>;
export type JobFunctions = Array<{
  id: string;
  text: string;
  usageRanking: string;
}>;
export type InternshipFunctions = Array<{
  id: string;
  text: string;
  usageRanking: string;
}>;
export type VolopFunctions = Array<{
  id: string;
  text: string;
  usageRanking: string;
}>;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createOrg(orgInfo: Record<string, any>): Promise<AdminOrg> {
  return postJson(`/data/website/organizations`, orgInfo).then(
    ({ org }) => org
  );
}
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function validateOrg(payload: Record<string, any>): Promise<{
  job: Job;
}> {
  return postJson(`/data/website/organizations-validation`, payload);
}
export function fetchOrg(orgId: string): Promise<Org> {
  return getJson(`/data/website/organizations/${orgId}`).then(
    ({ data, org }) => data || org
  );
}
export function requestAdmin(orgId: string, requestTo: string) {
  return postJson(`/data/website/organizations/${orgId}/request-admin`, {
    requestTo,
  });
}
export function fetchListing(
  listingType: string,
  listingId: string
): Promise<Listing> {
  return getJson(
    `/data/website/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}`
  ).then(
    ({ data, job, internship, volop, event }) =>
      data || job || internship || volop || event
  );
}
export function fetchAreasOfFocus(): Promise<AreasOfFocus> {
  return getJson(`/data/website/areas-of-focus?lang=${USER_LOCALE}`).then(
    ({ areasOfFocus }) => areasOfFocus
  );
}
export function fetchJobFunctions(): Promise<JobFunctions> {
  return getJson(`/data/website/job-functions?lang=${USER_LOCALE}`).then(
    ({ jobFunctions }) => jobFunctions
  );
}
export function fetchInternshipFunctions(): Promise<JobFunctions> {
  return getJson(`/data/website/internship-functions?lang=${USER_LOCALE}`).then(
    ({ internshipFunctions }) => internshipFunctions
  );
}
export function fetchVolopFunctions(): Promise<VolopFunctions> {
  return getJson(`/data/website/volop-functions?lang=${USER_LOCALE}`).then(
    ({ volopFunctions }) => volopFunctions
  );
}
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createOrgWithGradProgram(info: Record<string, any>): Promise<{
  org: AdminOrg;
  gradProgram: GradProgram;
}> {
  return postJson('/data/website/organizations-with-grad-program', info);
}
